import { type DefaultContext, mutationPromise } from '@egw/clients'
import { createMutationHook, createQueryHook } from '../../../../hooks'
import {
  type EnrollmentJointResendReenterVerifyPhoneSubmitInput,
  type EnrollmentJointReenterVerifyPhoneData,
  type EnrollmentJointReenterVerifyPhoneSubmitInput,
  type EnrollmentJointReenterVerifyPhoneSubmitOutput,
  type EnrollmentJointVerifyPhoneContinueApplicationOutput,
  type EnrollmentJointReenterVerifyPhoneInput,
} from '../../../../models/enrollment/joint/reenter-verify-phone'

/**
 * Get Enrollment Joint ReenterVerifyPhone data
 * @param input - ReentryInput
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointReenterVerifyPhoneData>
 * @example const response = await getEnrollmentJointReenterVerifyPhoneData()
 */
export const getEnrollmentJointReenterVerifyPhoneData = async (
  input?: EnrollmentJointReenterVerifyPhoneInput,
  context?: DefaultContext,
): Promise<EnrollmentJointReenterVerifyPhoneData> => {
  const response = (await mutationPromise(
    {
      name: 'getEnrollmentJointReenterVerifyPhoneData',
      query: `mutation getEnrollmentJointReenterVerifyPhoneData($input: ReentryInput!) {
        enterApplication(input: $input) {
          phone {
            countryPhoneCode
            phoneNumber
          }
          nextStep
          error
        }
      }`,
    },
    {
      input: input ?? {},
    },
    context,
  )) as { enterApplication: EnrollmentJointReenterVerifyPhoneData }
  return response.enterApplication
}

export const useGetEnrollmentJointReenterVerifyPhoneData = createQueryHook<
  EnrollmentJointReenterVerifyPhoneInput,
  EnrollmentJointReenterVerifyPhoneData
>(getEnrollmentJointReenterVerifyPhoneData)

/**
 * Submit Enrollment Joint ReenterVerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointReenterVerifyPhoneSubmitOutput>
 * @example const response = await submitEnrollmentJointReenterVerifyPhoneData();
 */
export const submitEnrollmentJointReenterVerifyPhoneData = async (
  enrollmentReenterVerifyPhoneSubmitInput: EnrollmentJointReenterVerifyPhoneSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointReenterVerifyPhoneSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointReenterVerifyPhoneData',
      query: `mutation submitEnrollmentJointReenterVerifyPhoneData($input: ReentryInput!) {
        enterApplication(input: $input) {
          phone {
            countryPhoneCode
            phoneNumber
          }
          nextStep
          error
        }
      }`,
    },
    {
      input: enrollmentReenterVerifyPhoneSubmitInput,
    },
    context,
  )) as { enterApplication: EnrollmentJointReenterVerifyPhoneSubmitOutput }
  return response.enterApplication
}

export const useSubmitEnrollmentJointReenterVerifyPhoneData = createMutationHook<
  EnrollmentJointReenterVerifyPhoneSubmitInput,
  EnrollmentJointReenterVerifyPhoneSubmitOutput
>(submitEnrollmentJointReenterVerifyPhoneData)

/**
 * Submit Enrollment Joint ReenterVerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointReenterVerifyPhoneSubmitOutput>
 * @example const response = await submitEnrollmentJointReenterVerifyPhoneData();
 */
export const submitEnrollmentJointResendReenterVerifyPhoneData = async (
  enrollmentResendReenterVerifyPhoneSubmitInput: EnrollmentJointResendReenterVerifyPhoneSubmitInput,
  context?: DefaultContext,
): Promise<EnrollmentJointReenterVerifyPhoneSubmitOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointResendReenterVerifyPhoneData',
      query: `mutation submitEnrollmentJointResendReenterVerifyPhoneData($input: ReentryInput!) {
        enterApplication(input: $input) {
          phone {
            countryPhoneCode
            phoneNumber
          }
          nextStep
          error
        }
      }`,
    },
    {
      input: enrollmentResendReenterVerifyPhoneSubmitInput,
    },
    context,
  )) as { enterApplication: EnrollmentJointReenterVerifyPhoneSubmitOutput }
  return response.enterApplication
}

export const useSubmitEnrollmentJointResendReenterVerifyPhoneData = createMutationHook<
  EnrollmentJointResendReenterVerifyPhoneSubmitInput,
  EnrollmentJointReenterVerifyPhoneSubmitOutput
>(submitEnrollmentJointResendReenterVerifyPhoneData)

/**
 * Submit Enrollment Joint ReenterVerifyPhone data
 * @param context - reference to the AbortController used to abort the call (optional)
 * @returns Promise<EnrollmentJointVerifyPhoneContinueApplicationOutput>
 * @example const response = await submitEnrollmentJointReenterVerifyPhoneOtpData();
 */
export const submitEnrollmentJointReenterVerifyPhoneOtpData = async (
  context?: DefaultContext,
): Promise<EnrollmentJointVerifyPhoneContinueApplicationOutput> => {
  const response = (await mutationPromise(
    {
      name: 'submitEnrollmentJointReenterVerifyPhoneOtpData',
      query: `mutation submitEnrollmentJointReenterVerifyPhoneOtpData($input: SubmitJointEnrollmentInput!) {
        submitJointEnrollment(input: $input) {
          errors
          isError
          activePerson {
            path
          }
        }
      }`,
    },
    {
      input: {},
    },
    context,
  )) as { submitJointEnrollment: EnrollmentJointVerifyPhoneContinueApplicationOutput }
  return response.submitJointEnrollment
}

export const useSubmitEnrollmentJointReenterVerifyPhoneOtpData = createMutationHook<
  undefined,
  EnrollmentJointVerifyPhoneContinueApplicationOutput
>(submitEnrollmentJointReenterVerifyPhoneOtpData)
